/* THEBLACKBOXHUB — facets.js
   Storefront filtering, sorting, load more and grid density for collection and search pages.
   Filters are a normal GET form, so everything works without JavaScript. */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const utils = window.themeUtils || {};

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  function showError() {
    if (utils.showToast) utils.showToast(theme.strings.network || 'Something went wrong.', true);
  }

  class FacetsRoot extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.sectionId;
      this.cache = {};
      this.debounced = utils.debounce ? utils.debounce(() => this.submit(), 450) : () => this.submit();

      const formFor = (el) => {
        if (el && el.form && el.form.matches('[data-facets-form]')) return el.form;
        return el && el.closest ? el.closest('form[data-facets-form]') : null;
      };
      this.addEventListener('change', (event) => {
        const form = formFor(event.target);
        if (!form) return;
        if (event.target.matches('input[type="number"]')) {
          this.lastForm = form;
          this.debounced();
        } else {
          this.submit(form);
        }
      });
      this.addEventListener('input', (event) => {
        const form = formFor(event.target);
        if (form && event.target.matches('input[type="number"]')) {
          this.lastForm = form;
          this.debounced();
        }
      });
      this.addEventListener('submit', (event) => {
        const form = event.target.closest('form[data-facets-form]');
        if (!form) return;
        event.preventDefault();
        this.submit(form);
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-facet-link]');
        if (!link) return;
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.render(url.searchParams.toString());
      });
      window.addEventListener('popstate', (event) => {
        const params = event.state && typeof event.state.params === 'string' ? event.state.params : window.location.search.slice(1);
        this.render(params, false);
      });
    }

    paramsFromForm(form) {
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((value, key) => {
        if (value === '' || value === null) return;
        params.append(key, value);
      });
      return params.toString();
    }

    submit(form) {
      form = form && form.tagName === 'FORM' ? form : this.lastForm || this.querySelector('form[data-facets-form]');
      if (!form) return;
      this.lastForm = form;
      this.render(this.paramsFromForm(form));
    }

    render(params, push) {
      if (push === undefined) push = true;
      const url = window.location.pathname + '?section_id=' + encodeURIComponent(this.sectionId) + (params ? '&' + params : '');
      const grid = this.querySelector('[data-product-grid-container]');
      if (grid) grid.classList.add('is-loading');
      const openDetails = Array.from(this.querySelectorAll('details[open][id]')).map((d) => d.id);
      const focusedId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

      const done = (html) => {
        this.cache[url] = html;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        [
          '[data-product-grid-container]',
          '[data-facets-desktop]',
          '[data-facets-mobile]',
          '[data-active-facets]',
          '[data-results-count]',
          '[data-results-button]',
        ].forEach((sel) => {
          const freshList = doc.querySelectorAll(sel);
          this.querySelectorAll(sel).forEach((current, i) => {
            const fresh = freshList[i] || freshList[0];
            if (fresh) current.innerHTML = fresh.innerHTML;
          });
        });
        openDetails.forEach((id) => {
          const d = document.getElementById(id);
          if (d) d.open = true;
        });
        if (focusedId) {
          const el = document.getElementById(focusedId);
          if (el) el.focus({ preventScroll: true });
        }
        if (push) {
          try {
            window.history.pushState({ params: params }, '', window.location.pathname + (params ? '?' + params : ''));
          } catch (e) {
            /* ignore */
          }
        }
        const fresh = this.querySelector('[data-product-grid-container]');
        if (fresh) fresh.classList.remove('is-loading');
      };

      if (this.cache[url]) return done(this.cache[url]);
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.text();
        })
        .then(done)
        .catch(() => {
          if (grid) grid.classList.remove('is-loading');
          showError();
        });
    }
  }
  define('facets-root', FacetsRoot);

  class LoadMore extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-load-more]');
        if (!link) return;
        event.preventDefault();
        this.load(link);
      });
    }

    load(link) {
      const root = this.closest('facets-root');
      const sectionId = root ? root.dataset.sectionId : this.dataset.sectionId;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      link.classList.add('is-loading');
      fetch(url.toString())
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = doc.querySelectorAll('[data-product-grid] > li');
          const grid = document.querySelector('[data-product-grid]');
          if (grid) items.forEach((item) => grid.appendChild(item));
          const fresh = doc.querySelector('load-more');
          if (fresh) this.innerHTML = fresh.innerHTML;
          else this.remove();
        })
        .catch(() => {
          link.classList.remove('is-loading');
          showError();
        });
    }
  }
  define('load-more', LoadMore);

  class GridDensity extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const key = 'tbbh-grid-density';
      const saved = utils.storageGet ? utils.storageGet(key, null) : null;
      if (saved && saved.mobile) this.apply('mobile', saved.mobile);
      if (saved && saved.desktop) this.apply('desktop', saved.desktop);
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-density]');
        if (!btn) return;
        const device = btn.dataset.device;
        const cols = btn.dataset.density;
        this.apply(device, cols);
        const state = (utils.storageGet && utils.storageGet(key, {})) || {};
        state[device] = cols;
        if (utils.storageSet) utils.storageSet(key, state);
      });
    }

    apply(device, cols) {
      const root = this.closest('facets-root') || document;
      root.style.setProperty(device === 'mobile' ? '--cols-mobile' : '--cols-desktop', cols);
      this.querySelectorAll('[data-device="' + device + '"]').forEach((b) => {
        b.setAttribute('aria-pressed', b.dataset.density === String(cols) ? 'true' : 'false');
      });
    }
  }
  define('grid-density', GridDensity);
})();
